/**
 * WaitlistForm - the signup on the Meta ad pages. Name, email and one button
 * open the rest of the form: questions about the practice (role and
 * providers; /ehr adds the current EHR). The signup is sent only once every
 * question is answered, and then a confirmation takes the form's place with
 * the site's Cal.com booking calendar under it, filled in with the name,
 * email and answers.
 *
 * On desktop the fields and the button share one pill, the surface of the
 * homepage's receipt pill; below 900px they stack. Each signup is a row in
 * the waitlist Sheet (src/lib/waitlist.ts), answers included. Lead (Meta) and
 * generate_lead (GA4) fire on the signup, only for visitors who accepted
 * cookies, because neither tag loads before that. The calendar shares the
 * Talk to us pop-up's Cal.com namespace, so it has the same look and the same
 * booking events.
 */

import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import Cal from "@calcom/embed-react";
import { Check } from "lucide-react";
import { buildPrefillNotes, readReferral } from "@/lib/book-a-call";
import { CAL_NAMESPACE, CAL_TEAM_LINK } from "@/lib/cal-config";
import { EMAIL_PATTERN, joinWaitlist, type WaitlistAnswers, type WaitlistPage } from "@/lib/waitlist";
import { answersNote, readAnswers, type AnswerField } from "./waitlist-questions";
import { WaitlistQuestionFields } from "./WaitlistQuestions";

type WaitlistField = "name" | "email";

const FIELDS: {
  name: WaitlistField;
  label: string;
  placeholder: string;
  type: "text" | "email";
  autoComplete: string;
  maxLength: number;
  /** Share of the desktop pill's width. */
  grow: number;
}[] = [
  { name: "name", label: "Name", placeholder: "Your name", type: "text", autoComplete: "name", maxLength: 120, grow: 1 },
  { name: "email", label: "Email", placeholder: "Email", type: "email", autoComplete: "email", maxLength: 200, grow: 1.3 },
];

/* Same classes as BookACallButton's filled "lg" pill, so the submit button
   and the confirmation's "Talk to us" button match the site's CTA. */
const FILLED_LG_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-full text-[16px] font-semibold transition-all duration-150 bg-foreground text-background hover:opacity-85 cursor-pointer border-0 whitespace-nowrap";

type Values = Record<WaitlistField, string>;
type Problem = { field: WaitlistField | AnswerField | null; message: string };

function readValues(data: FormData): Values {
  const read = (field: WaitlistField) => String(data.get(field) ?? "").trim();
  return { name: read("name"), email: read("email") };
}

/** The first problem in the form, checking the questions once they show. */
function findProblem(data: FormData, questions: { askCurrentEhr: boolean } | null): Problem | null {
  const values = readValues(data);
  if (!values.name) return { field: "name", message: "Please add your name." };
  if (!EMAIL_PATTERN.test(values.email))
    return { field: "email", message: "Please check your email address." };
  const missing = questions && readAnswers(data, questions.askCurrentEhr).missing;
  if (missing) return { field: missing, message: "Please answer each question." };
  return null;
}

/** The booking form opens with the signup's name, email and answers filled
    in. The look (light, month view) is the namespace's, set in main.tsx. */
function calConfig({ name, email, answers }: Values & { answers: WaitlistAnswers }) {
  const notes = [buildPrefillNotes(readReferral()), answersNote(answers)].filter(Boolean).join("\n");
  return { name, email, notes };
}

interface WaitlistFormProps {
  page: WaitlistPage;
  /** The first button, which opens the questions, and the last, which sends the signup. */
  submitLabel: string;
  /** Under the form until it is sent. */
  note?: ReactNode;
  doneTitle: string;
  /** The confirmation when the Sheet returns the signup's place in line. */
  placeTitle?: (place: number) => string;
  /** Between the confirmation and the calendar. */
  doneNote?: ReactNode;
  /** Desktop width of the pill and the questions card. */
  maxWidth: number;
  /** Adds the current EHR question. */
  askCurrentEhr?: boolean;
}

type Signup = Values & { answers: WaitlistAnswers; place: number | null };

export function WaitlistForm({
  page,
  submitLabel,
  note,
  doneTitle,
  placeTitle,
  doneNote,
  maxWidth,
  askCurrentEhr = false,
}: WaitlistFormProps) {
  const [status, setStatus] = useState<"start" | "questions" | "sending" | "done">("start");
  const [problem, setProblem] = useState<Problem | null>(null);
  const [signup, setSignup] = useState<Signup | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLParagraphElement>(null);
  const errorId = useId();
  const unlocked = status === "questions" || status === "sending";
  const done = status === "done";

  /* The questions, then the calendar, are the next step, so bring the form
     to the top of the screen with them under it, and move focus to the line
     that introduces them. Only the window scrolls: scrollIntoView would also
     scroll the hero, whose overflow is hidden. */
  useEffect(() => {
    if (!unlocked && !done) return;
    const form = formRef.current;
    if (!form) return;
    headingRef.current?.focus({ preventScroll: true });
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({
      top: window.scrollY + form.getBoundingClientRect().top - 30,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }, [unlocked, done]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "sending") return;
    const form = e.currentTarget;
    const data = new FormData(form);

    const found = findProblem(data, unlocked ? { askCurrentEhr } : null);
    setProblem(found);
    if (found?.field) {
      form.querySelector<HTMLInputElement>(`input[name="${found.field}"]`)?.focus();
      return;
    }
    if (!unlocked) {
      setStatus("questions");
      return;
    }

    const values = readValues(data);
    const { answers } = readAnswers(data, askCurrentEhr);
    if (!answers) return;
    setStatus("sending");
    let place: number | null;
    try {
      place = await joinWaitlist({ page, ...values, ...answers });
    } catch {
      setStatus("questions");
      setProblem({ field: null, message: "Something went wrong. Please try again." });
      return;
    }
    window.fbq?.("track", "Lead");
    window.gtag?.("event", "generate_lead", { lead_source: page });
    setSignup({ ...values, answers, place });
    setStatus("done");
  }

  /* Once the questions show, a problem clears as soon as it is fixed. */
  function recheck(e: FormEvent<HTMLFormElement>) {
    if (unlocked && problem?.field) setProblem(findProblem(new FormData(e.currentTarget), { askCurrentEhr }));
  }

  const error = problem && (
    <p id={errorId} role="alert" className="wl-error">
      {problem.message}
    </p>
  );

  return (
    <div
      ref={formRef}
      className={done ? "wl-form wl-form-done" : "wl-form"}
      style={{ "--wl-max": `${maxWidth}px` } as React.CSSProperties}
    >
      {done && signup ? (
        <>
          <div className="wl-bar">
            <p ref={headingRef} tabIndex={-1} className="wl-done">
              <Check aria-hidden="true" size={20} strokeWidth={2.5} style={{ color: "hsl(var(--accent-emerald-ink))", flexShrink: 0 }} />
              {signup.place !== null && placeTitle ? placeTitle(signup.place) : doneTitle}
            </p>
          </div>
          {doneNote && <p className="wl-note">{doneNote}</p>}
          {/* Cal renders nothing until its script loads; the wrapper holds the
              space so the page is tall enough to scroll from the start. */}
          <div className="wl-cal">
            <Cal namespace={CAL_NAMESPACE} calLink={CAL_TEAM_LINK} config={calConfig(signup)} />
          </div>
        </>
      ) : (
        <form noValidate onSubmit={handleSubmit} onChange={recheck} aria-busy={status === "sending"}>
          <div className="wl-bar">
            {FIELDS.map((f) => {
              const invalid = problem?.field === f.name;
              return (
                <label
                  key={f.name}
                  className="wl-field"
                  style={{ "--wl-grow": f.grow } as React.CSSProperties}
                >
                  <span className="sr-only">{f.label}</span>
                  <input
                    className="wl-input"
                    name={f.name}
                    type={f.type}
                    placeholder={f.placeholder}
                    autoComplete={f.autoComplete}
                    maxLength={f.maxLength}
                    aria-invalid={invalid || undefined}
                    aria-describedby={invalid ? errorId : undefined}
                  />
                </label>
              );
            })}
            {!unlocked && (
              <button type="submit" className={`${FILLED_LG_CLASS} wl-submit`}>
                {submitLabel}
              </button>
            )}
          </div>
          {unlocked ? (
            <>
              <p ref={headingRef} tabIndex={-1} className="wl-note wl-q-intro">
                Almost there. A few quick questions about your practice.
              </p>
              <div className="wl-questions">
                <WaitlistQuestionFields
                  askCurrentEhr={askCurrentEhr}
                  invalid={problem?.field ?? null}
                  errorId={errorId}
                />
                {error}
                <div className="wl-q-actions">
                  <button
                    type="submit"
                    className={`${FILLED_LG_CLASS} wl-q-submit`}
                    disabled={status === "sending"}
                  >
                    {submitLabel}
                  </button>
                </div>
              </div>
            </>
          ) : (
            error
          )}
          {note && <p className="wl-note">{note}</p>}
        </form>
      )}

      <style>{`
        .wl-form {
          width: 100%;
          max-width: 440px;
          margin-top: 44px;
          text-align: center;
        }
        .wl-bar {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .wl-field {
          display: block;
          position: relative;
        }
        .wl-input,
        .wl-done {
          width: 100%;
          height: 52px;
          padding: 0 22px;
          border-radius: 999px;
          background: hsl(40 30% 98%);
          border: 1px solid hsl(225 45% 14% / 0.12);
          box-shadow: 0 2px 12px -7px hsl(225 45% 14% / 0.25);
          font-family: 'Outfit', sans-serif;
          font-size: 16px;
          color: hsl(225 45% 14%);
        }
        .wl-input {
          --wl-fill: 0 0 #0000;
          box-shadow: var(--wl-fill), 0 2px 12px -7px hsl(225 45% 14% / 0.25);
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .wl-input::placeholder {
          color: hsl(225 45% 14% / 0.5);
        }
        /* The early access form's focus ring. */
        .wl-input:focus {
          border-color: transparent;
          box-shadow: var(--wl-fill), 0 0 0 2px hsl(var(--ring) / 0.5);
        }
        .wl-input[aria-invalid="true"] {
          border-color: hsl(var(--accent-rose-ink) / 0.7);
        }
        .wl-input[aria-invalid="true"]:focus {
          border-color: transparent;
          box-shadow: var(--wl-fill), 0 0 0 2px hsl(var(--accent-rose-ink) / 0.5);
        }
        /* Browsers tint autofilled fields blue or yellow with a background
           pages cannot override; an inset shadow in the field's colour
           covers it. */
        .wl-input:-webkit-autofill {
          --wl-fill: inset 0 0 0 60px hsl(40 30% 98%);
          background-clip: padding-box;
          -webkit-text-fill-color: hsl(225 45% 14%);
        }
        .wl-done {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin: 0;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 18px;
          font-weight: 600;
          letter-spacing: -0.01em;
          outline: none;
        }
        .wl-submit {
          height: 52px;
          padding: 0 28px !important;
          margin-top: 2px;
        }
        .wl-q-submit:disabled {
          opacity: 0.6;
          cursor: progress;
        }
        .wl-error {
          margin: 14px 0 0;
          font-size: 14px;
          line-height: 1.5;
          color: hsl(var(--accent-rose-ink));
        }
        .wl-note {
          margin: 16px 0 0;
          font-family: 'JetBrains Mono', monospace;
          font-size: 13px;
          line-height: 1.5;
          letter-spacing: -0.01em;
          color: hsl(225 45% 14% / 0.72);
          text-wrap: balance;
        }
        /* After a signup the calendar takes the full width; the confirmation
           and its note keep the form's. */
        .wl-form-done {
          max-width: none;
        }
        .wl-form-done > .wl-bar,
        .wl-form-done > .wl-note {
          max-width: 440px;
          margin-left: auto;
          margin-right: auto;
        }
        /* The calendar grows to about 1,700px on phones once it loads; scroll
           anchoring would follow the content under it and carry the page far
           past the confirmation. */
        body:has(.wl-form-done) {
          overflow-anchor: none;
        }
        .wl-cal {
          width: 100%;
          min-height: 480px;
          margin-top: 24px;
        }
        /* The desktop pill's surface, as a card. */
        .wl-questions {
          margin-top: 20px;
          padding: 30px 32px 28px;
          border-radius: 24px;
          background: hsl(40 30% 98%);
          border: 1px solid hsl(225 45% 14% / 0.1);
          box-shadow: 0 10px 30px -14px hsl(225 45% 14% / 0.3);
          text-align: left;
        }
        .wl-questions .wl-error {
          margin-top: 20px;
        }
        /* Takes focus for screen readers when the questions open. */
        .wl-q-intro {
          outline: none;
        }
        .wl-q-actions {
          display: flex;
          justify-content: flex-end;
          margin-top: 28px;
        }
        .wl-q-submit {
          height: 52px;
          padding: 0 32px;
        }
        @media (max-width: 768px) {
          .wl-form {
            margin-top: 28px;
          }
          .wl-questions {
            padding: 24px 20px 22px;
            border-radius: 22px;
          }
          .wl-q-actions {
            flex-direction: column;
            margin-top: 24px;
          }
        }
        @media (min-width: 900px) {
          .wl-form {
            max-width: var(--wl-max);
          }
          .wl-form-done {
            max-width: 1000px;
          }
          .wl-form-done > .wl-bar,
          .wl-form-done > .wl-note {
            max-width: var(--wl-max);
          }
          .wl-bar {
            flex-direction: row;
            align-items: center;
            gap: 0;
            padding: 6px;
            border-radius: 999px;
            background: hsl(40 30% 98%);
            border: 1px solid hsl(225 45% 14% / 0.1);
            box-shadow: 0 10px 30px -14px hsl(225 45% 14% / 0.3);
            transition: border-color 0.15s, box-shadow 0.15s;
          }
          /* The fields share one pill, so focus rings the pill, not the
             field. The confirmation takes focus for screen readers but is
             not a field, so only the form's pill rings. */
          form .wl-bar:focus-within {
            border-color: transparent;
            box-shadow: 0 0 0 2px hsl(var(--ring) / 0.5), 0 10px 30px -14px hsl(225 45% 14% / 0.3);
          }
          .wl-field {
            flex: var(--wl-grow) 1 0;
            min-width: 0;
          }
          .wl-field + .wl-field::before {
            content: "";
            position: absolute;
            left: 0;
            top: 50%;
            width: 1px;
            height: 24px;
            transform: translateY(-50%);
            background: hsl(225 45% 14% / 0.12);
          }
          .wl-input,
          .wl-done {
            background: transparent;
            border: 0;
            box-shadow: none;
          }
          .wl-input,
          .wl-input:focus,
          .wl-input[aria-invalid="true"]:focus {
            box-shadow: var(--wl-fill);
          }
          .wl-done {
            flex: 1;
          }
          .wl-submit {
            flex-shrink: 0;
            margin: 0 0 0 6px;
          }
        }
      `}</style>
    </div>
  );
}
